import { useMemo } from 'react';
import { useAvContext } from '@context/AvContextProvider';
import { FeatureFlags } from '@fabric-ui/components';
import { ArrowDown } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { ellipsis, envVariables } from '@fabric-ui/utils';
import { Box, Tooltip } from '@mui/material';
import { createFileRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { useNavigate } from '@utils/AvRouter';
import { removeConsecutiveRepeats } from '@utils/breadcrumbsUtils';

export const Route = createFileRoute('/_breadcrumbsLayout')({
  component: BreadcrumbsBar,
});

const BREADCRUMBS_BAR_HEIGHT = 18;
const BC_EXTRA_BOTTOM_PADDING = 3;
const PAGE_SHIFT_HEIGHT = 24;
export const FULL_BREADCRUMBS_HEIGHT = PAGE_SHIFT_HEIGHT + BREADCRUMBS_BAR_HEIGHT + BC_EXTRA_BOTTOM_PADDING;

type BreadcrumbContext = {
  getTitle?: () => string;
  hideBreadcrumbs?: boolean;
  preserveSearchParams?: boolean;
};

function BreadcrumbsBar() {
  const navigate = useNavigate();
  const matches = useRouterState({ select: s => s.matches });
  const crumbs = removeConsecutiveRepeats(matches, m => m.context?.getTitle?.())
    .filter(m => m.context?.getTitle?.())
    .map(m => ({
      hideBreadcrumbs: m.context.hideBreadcrumbs,
      text: m.context.getTitle?.(),
      subtitle: '',
      onClick: () => {
        const context = m.context as BreadcrumbContext;
        const preserveSearch = context.preserveSearchParams !== false;
        navigate({ to: m.fullPath, search: preserveSearch });
      },
    }));
  const { featureFlags } = useAvContext();
  const breadcrumbsFeatureFlagIsTrue = featureFlags[FeatureFlags.breadcrumbs];
  const hasMoreThanOneCrumb = crumbs.length > 1;
  const noHideOverride = !crumbsHideOverride(crumbs);
  const shouldShowBreadcrumbs = noHideOverride && breadcrumbsFeatureFlagIsTrue && hasMoreThanOneCrumb;

  const path = useMemo(() => crumbs.map(({ text }) => text).join(' - '), [crumbs]);

  return (
    <>
      <title>{`Zscaler Agentic SecOps Platform${envVariables.VITE_ENV === 'stg' ? ' STG' : ''}${path ? ` | ${path}` : ''}`}</title>
      {shouldShowBreadcrumbs ? (
        <Box
          className="breadcrumbs-component"
          sx={{
            ...flex.row,
            alignItems: 'end',
            width: '100%',
            position: 'absolute',
            height: `${FULL_BREADCRUMBS_HEIGHT}px`,
            zIndex: 1,
            '+ *': {
              borderTop: `${FULL_BREADCRUMBS_HEIGHT}px solid transparent`,
            },
            paddingBottom: `${BC_EXTRA_BOTTOM_PADDING}px`,
          }}>
          {crumbs.map((crumb, i) => {
            const isCurrent = i === crumbs.length - 1;
            return (
              <Box sx={{ ...flex.itemsCenter }}>
                <Box
                  role="button"
                  sx={{
                    marginLeft: +(i > 0),
                    marginRight: 1,
                    cursor: isCurrent ? 'default' : 'pointer',
                    '&:hover *': { textDecoration: isCurrent ? 'none' : 'underline' },
                  }}
                  onClick={!isCurrent ? crumb.onClick : undefined}>
                  <Breadcrumb text={`${crumb.text}`} isCurrent={isCurrent} />
                </Box>
                {!isCurrent && divider()}
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box className="bc-placeholder" />
      )}

      <Outlet />
    </>
  );
}

function Breadcrumb({ text, isCurrent = false }: { text: string; isCurrent: boolean }) {
  const fullTextLength = text.length + 1;
  return (
    <Tooltip title={text} disableHoverListener={fullTextLength < 25} placement="bottom">
      <Box sx={{ ...flex.itemsCenter, maxWidth: '25ch' }}>
        <Box
          sx={{
            color: isCurrent ? 'var(--color-content-base-primary)' : 'var(--color-content-interactive-primary-default)',
            marginLeft: 0,
            fontWeight: isCurrent ? 600 : 400,
            ...ellipsis,
          }}>
          {text}
        </Box>
      </Box>
    </Tooltip>
  );
}

const divider = () => (
  <ArrowDown style={{ transform: 'rotate(270deg)', color: 'var(--color-content-base-secondary)', height: 16, width: 16 }} />
);

const crumbsHideOverride = (
  crumbs: {
    text: string;
    subtitle: string;
    onClick: () => void;
    hideBreadcrumbs: boolean;
  }[]
) => crumbs.some(crumb => crumb.hideBreadcrumbs);
